import React, { useEffect, useState } from "react";
import AdminLayout from "src/layout/admin";
import GenericDataLayout from "src/components/custom/GenericDataLayout";
import GenericTable from "src/components/custom/GenericTable";
import Modal from "src/components/modal";
import Button from "src/components/custom/Button";
import ItiFormFields from "src/components/mcc/ItiFormFields";
import { ItiFilterSuggestionSelect } from "src/components/mcc/ITIFilterSelect";
import {
  getMethodApiCall,
  postMethodApiCall,
  putMethodApiCall,
} from "src/services/api";
import { toast } from "react-toastify";
import {
  ITI_TABLE_COLUMNS,
  emptyItiAddForm,
  emptyItiEditForm,
  validateItiAddForm,
  buildAddItiPayload,
  buildUpdateItiPayload,
  mapItiDetailToForm,
} from "src/utils/mcc/itiHelper";

const TOAST_DURATION_MS = 7000;

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-3 border-b border-gray-100 py-2.5 last:border-0">
    <span className="text-xs font-semibold text-primary">{label}</span>
    <span className="text-sm text-gray-800 break-words">{value ?? "—"}</span>
  </div>
);

const Iti = () => {
  const [tableRows, setTableRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState(() => emptyItiAddForm());
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailViewMode, setDetailViewMode] = useState("view");
  const [itiDetailRaw, setItiDetailRaw] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editForm, setEditForm] = useState(() => emptyItiEditForm());
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editTradenameOptions, setEditTradenameOptions] = useState([]);

  const [tradenameOptions, setTradenameOptions] = useState([]);

  const [filterDistrict, setFilterDistrict] = useState("");
  const [filterItiId, setFilterItiId] = useState("");

  const loadItis = async (
    page = pagination.page,
    limit = pagination.limit,
    dist = filterDistrict,
    iti = filterItiId
  ) => {
    setListLoading(true);
    try {
      const params = { page, limit };
      if (dist) params.district = dist;
      if (iti) params.iti_id = iti;
      const res = await getMethodApiCall("/v1/api/mcc/itis", params);
      if (res?.status === 200 && res?.data?.success) {
        setTableRows(res.data.data || []);
        const p = res.data.pagination || {};
        setPagination((prev) => ({
          ...prev,
          page: p.page ?? page,
          limit: p.limit ?? limit,
          totalRecords: p.total ?? 0,
        }));
      } else {
        setTableRows([]);
        toast.error(res?.data?.message || "Failed to load ITI list", {
          autoClose: TOAST_DURATION_MS,
        });
      }
    } catch {
      setTableRows([]);
      toast.error("Failed to load ITI list", { autoClose: TOAST_DURATION_MS });
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadItis(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchAllTrades = async () => {
      const res = await getMethodApiCall(`/v1/api/mcc/trades/picklist`);
      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.trade_name)
          .map((item) => ({
            label: item.trade_name,
            value: String(item.id),
          }));
        setTradenameOptions(options);
      } else {
        setTradenameOptions([]);
        setCreateForm((prev) => ({ ...prev, tradename: [] }));
      }
    };
    if (addModalOpen) fetchAllTrades();
  }, [addModalOpen]);

  useEffect(() => {
    const fetchEditTrades = async () => {
      if (!detailModalOpen) {
        setEditTradenameOptions([]);
        return;
      }
      const res = await getMethodApiCall(`/v1/api/mcc/trades/picklist`);
      if (res?.status === 200 && res?.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => item?.trade_name)
          .map((item) => ({
            label: item.trade_name,
            value: String(item.id),
          }));
        setEditTradenameOptions(options);
      } else {
        setEditTradenameOptions([]);
      }
    };
    fetchEditTrades();
  }, [detailModalOpen]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadItis(newPage, pagination.limit, filterDistrict, filterItiId);
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    loadItis(1, newLimit, filterDistrict, filterItiId);
  };

  const handleResetItiFilters = () => {
    setFilterDistrict("");
    setFilterItiId("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadItis(1, pagination.limit, "", "");
  };

  const openAddModal = () => {
    clearCreateForm();
    setAddModalOpen(true);
  };

  const closeAddModal = () => setAddModalOpen(false);

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailViewMode("view");
    setItiDetailRaw(null);
    setEditForm(emptyItiEditForm());
    setEditTradenameOptions([]);
  };

  const clearCreateForm = () => {
    setCreateForm(emptyItiAddForm());
    setTradenameOptions([]);
  };

  const handleCreateIti = async () => {
    const v = validateItiAddForm(createForm);
    if (!v.ok) {
      toast.error(v.message, { autoClose: TOAST_DURATION_MS });
      return;
    }
    setCreateSubmitting(true);
    try {
      const payload = buildAddItiPayload(createForm);
      const res = await postMethodApiCall("/v1/api/mcc/itis", payload);
      if ((res?.status === 200 || res?.status === 201) && res?.data?.success) {
        toast.success(res.data.message || "ITI created successfully", {
          autoClose: TOAST_DURATION_MS,
        });
        clearCreateForm();
        setAddModalOpen(false);
        await loadItis(
          1,
          pagination.limit,
          filterDistrict,
          filterItiId
        );
      } else {
        toast.error(res?.data?.message || res?.data?.error || "Failed to create ITI", {
          autoClose: TOAST_DURATION_MS,
        });
      }
    } catch {
      toast.error("Failed to create ITI", { autoClose: TOAST_DURATION_MS });
    } finally {
      setCreateSubmitting(false);
    }
  };

  const reloadDetailFromServer = async (id) => {
    const res = await getMethodApiCall(`/v1/api/mcc/itis/${id}`);
    if (res?.status === 200 && res?.data?.success && res.data.data) {
      setItiDetailRaw(res.data.data);
      setEditForm(mapItiDetailToForm(res.data.data));
      return true;
    }
    toast.error(res?.data?.message || "Failed to load ITI details", {
      autoClose: TOAST_DURATION_MS,
    });
    return false;
  };

  const handleRowClick = async (row) => {
    if (!row?.id) return;
    setDetailLoading(true);
    setDetailModalOpen(true);
    setDetailViewMode("view");
    try {
      await reloadDetailFromServer(row.id);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateIti = async () => {
    const v = validateItiAddForm(editForm);
    if (!v.ok) {
      toast.error(v.message, { autoClose: TOAST_DURATION_MS });
      return;
    }
    if (!editForm.id) {
      toast.error("Missing ITI id", { autoClose: TOAST_DURATION_MS });
      return;
    }

    setEditSubmitting(true);
    try {
      const payload = buildUpdateItiPayload(editForm);
      const res = await putMethodApiCall(`/v1/api/mcc/itis/${editForm.id}`, payload);
      if (res?.status === 200 && res?.data?.success) {
        toast.success(res?.data?.message || "ITI updated successfully", {
          autoClose: TOAST_DURATION_MS,
        });
        const nextId =
          res?.data?.data?.effective_iti_id != null
            ? String(res.data.data.effective_iti_id)
            : res?.data?.data?.id != null
              ? String(res.data.data.id)
              : editForm.id;
        await reloadDetailFromServer(nextId);
        setDetailViewMode("view");
        await loadItis(pagination.page, pagination.limit, filterDistrict, filterItiId);
      } else {
        toast.error(res?.data?.message || res?.data?.error || "Failed to update ITI", {
          autoClose: TOAST_DURATION_MS,
        });
      }
    } catch {
      toast.error("Failed to update ITI", { autoClose: TOAST_DURATION_MS });
    } finally {
      setEditSubmitting(false);
    }
  };

  const cancelEditBackToView = async () => {
    if (!editForm.id) {
      closeDetailModal();
      return;
    }
    setDetailLoading(true);
    try {
      await reloadDetailFromServer(editForm.id);
      setDetailViewMode("view");
    } finally {
      setDetailLoading(false);
    }
  };

  const actions = [
    {
      label: "Add",
      onClick: (e) => {
        e.preventDefault?.();
        openAddModal();
      },
    },
  ];
  const itiFilterConfig = [
    {
      type: "custom",
      render: () => (
        <ItiFilterSuggestionSelect
          label="District"
          field="district"
          value={filterDistrict}
          onChange={(v) => {
            setFilterDistrict(v);
            setFilterItiId("");
            setPagination((p) => ({ ...p, page: 1 }));
            loadItis(1, pagination.limit, v, "");
          }}
          placeholder="Search district…"
          allLabel="All"
        />
      ),
    },
    {
      type: "custom",
      render: () => (
        <ItiFilterSuggestionSelect
          label="ITI"
          field="iti"
          value={filterItiId}
          onChange={(v) => {
            setFilterItiId(v);
            setPagination((p) => ({ ...p, page: 1 }));
            loadItis(1, pagination.limit, filterDistrict, v);
          }}
          placeholder={filterDistrict ? "Search ITI…" : "Select district first"}
          allLabel="All ITIs"
          disabled={!filterDistrict}
          districtParam={filterDistrict}
        />
      ),
    },
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        title="ITI"
        subtitle=""
        actions={actions}
        filters={itiFilterConfig}
        onResetFilters={handleResetItiFilters}
        isLoading={listLoading}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      >
        <GenericTable
          columns={ITI_TABLE_COLUMNS}
          data={tableRows}
          pagination={pagination}
          onRowClick={handleRowClick}
        />
      </GenericDataLayout>

      <Modal isOpen={addModalOpen} onClose={closeAddModal} title="Add ITI">
        <ItiFormFields
          form={createForm}
          setForm={setCreateForm}
          tradenameOptions={tradenameOptions}
        />
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={clearCreateForm}
            disabled={createSubmitting}
            className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-primary text-primary font-semibold shadow-sm hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition ease-in duration-200 disabled:opacity-50"
          >
            Clear
          </button>
          <Button
            title={createSubmitting ? "Creating..." : "Create"}
            disabled={createSubmitting}
            onClick={handleCreateIti}
          />
        </div>
      </Modal>

      <Modal
        isOpen={detailModalOpen}
        onClose={closeDetailModal}
        title={detailViewMode === "view" ? "ITI details" : "Edit ITI"}
      >
        {detailLoading && <p className="text-sm text-gray-500 py-4">Loading…</p>}

        {!detailLoading && itiDetailRaw && detailViewMode === "view" && (
          <>
            <div className="space-y-0 pr-1">
              <DetailRow label="ITI name" value={itiDetailRaw.iti_name} />
              <DetailRow label="District" value={itiDetailRaw.district} />
              <DetailRow label="Username" value={itiDetailRaw.username} />
              <DetailRow label="Password" value={itiDetailRaw.password} />
              <DetailRow
                label="Trades"
                value={
                  Array.isArray(itiDetailRaw.trade_names) &&
                  itiDetailRaw.trade_names.length > 0
                    ? itiDetailRaw.trade_names.join(", ")
                    : "—"
                }
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDetailModal}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-gray-300 text-gray-700 font-semibold shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition ease-in duration-200"
              >
                Cancel
              </button>
              <Button title="Edit" onClick={() => setDetailViewMode("edit")} />
            </div>
          </>
        )}

        {!detailLoading && itiDetailRaw && detailViewMode === "edit" && (
          <>
            <ItiFormFields
              form={editForm}
              setForm={setEditForm}
              tradenameOptions={editTradenameOptions}
            />
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={cancelEditBackToView}
                disabled={editSubmitting}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-gray-300 text-gray-700 font-semibold shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition ease-in duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                title={editSubmitting ? "Updating..." : "Update"}
                disabled={editSubmitting}
                onClick={handleUpdateIti}
              />
            </div>
          </>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default Iti;
